"use client"
import { X, MessageSquare, User, MapPin, Wrench } from 'lucide-react'

interface TextUsModalProps {
  isOpen: boolean
  onClose: () => void
  onContinue: () => void
}

export default function TextUsModal({ isOpen, onClose, onContinue }: TextUsModalProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 p-2 rounded-full">
                <MessageSquare className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold">Text Us</h3>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition-colors p-1">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <p className="text-gray-600 text-sm leading-relaxed">When you text us, please include:</p>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-start space-x-3">
              <div className="bg-red-50 p-2 rounded-full flex-shrink-0">
                <User className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 text-sm">Your Name</h4>
                <p className="text-gray-600 text-xs">So we know who we're helping</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="bg-red-50 p-2 rounded-full flex-shrink-0">
                <MapPin className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 text-sm">Your Address</h4>
                <p className="text-gray-600 text-xs">To schedule your service visit</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="bg-red-50 p-2 rounded-full flex-shrink-0">
                <Wrench className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 text-sm">Issue Description</h4>
                <p className="text-gray-600 text-xs">What appliance needs repair</p>
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 text-xs">
              <strong>Tip:</strong> The more details you provide, the better we can help you!
            </p>
          </div>

          {/* Action Button */}
          <div className="flex justify-center">
            <button
              onClick={onContinue}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2"
            >
              <MessageSquare className="h-4 w-4" />
              <span>Continue to Text</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
